import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { evaluatedPolicySelectors } from '../redux/modules/evaluatedPolicy';
import {
  AI_KB_INTERNAL_RESOURCE_NAME,
  AI_KB_MEMBERS_RESOURCE_NAME,
  canReadAiKbPath,
  canUpdateAiKbPath,
} from '../util/abac';
import { useActiveProject } from './useActiveProject';
import { useFeatures } from './useFeatures';

/**
 * Zori Knowledge Base tab: requires `zoriKnowledgeBase` (per-user) and prefix-specific
 * ABAC on `.ai-kb-members` / `.ai-kb-internal` file paths in the active project (FLDM-1421).
 */
export function useZoriKnowledgeBaseAccess(): {
  canViewTab: boolean;
  canReadMembersKb: boolean;
  canReadInternalKb: boolean;
  canManageMembersKb: boolean;
  canManageInternalKb: boolean;
  projectId: string | undefined;
} {
  const policy = useSelector(evaluatedPolicySelectors.selectPolicyForUser);
  const hasFeature = useFeatures();
  const [activeProject] = useActiveProject();
  const projectId = activeProject?.id;

  return useMemo(() => {
    const featureOk = hasFeature('zoriKnowledgeBase');
    if (!featureOk || !projectId) {
      return {
        canViewTab: false,
        canReadMembersKb: false,
        canReadInternalKb: false,
        canManageMembersKb: false,
        canManageInternalKb: false,
        projectId,
      };
    }

    const canReadMembersKb = canReadAiKbPath(
      policy,
      projectId,
      AI_KB_MEMBERS_RESOURCE_NAME,
    );
    const canReadInternalKb = canReadAiKbPath(
      policy,
      projectId,
      AI_KB_INTERNAL_RESOURCE_NAME,
    );
    const canViewTab = Boolean(canReadMembersKb || canReadInternalKb);

    const canManageMembersKb = canUpdateAiKbPath(
      policy,
      projectId,
      AI_KB_MEMBERS_RESOURCE_NAME,
    );
    const canManageInternalKb = canUpdateAiKbPath(
      policy,
      projectId,
      AI_KB_INTERNAL_RESOURCE_NAME,
    );

    return {
      canViewTab,
      canReadMembersKb,
      canReadInternalKb,
      canManageMembersKb,
      canManageInternalKb,
      projectId,
    };
  }, [hasFeature, policy, projectId]);
}

export default useZoriKnowledgeBaseAccess;
